/**
 * Theme JS entry point.
 *
 * UIkit and the Icons plugin are initialised here.
 * The compiled output (assets/js/main.js) is enqueued by functions.php —
 * never hardcode it in a template.
 */

import UIkit from 'uikit';
import Icons from 'uikit/dist/js/uikit-icons';
import initCounters from './counter.js';
import initFeaturedPostSliders from './featured-post-slider.js';
import initCodeBlocks from './code-block.js';
import initClipboard from './clipboard.js';
import initLucide from './lucide.js';
import initBlogLoadMore, { initLoadMore } from './blog-archive.js';
import initVideoCards from './video-cards.js';
import initCourseGrid from './course-grid.js';

// Load the Icons plugin so uk-icon and uk-navbar-toggle-icon work.
UIkit.use( Icons );

// Expose UIkit globally so separately-bundled scripts (e.g. newsletter-modal.js)
// can access it. Webpack bundles UIkit as a private module and does NOT set
// window.UIkit automatically — we do it explicitly here.
window.UIkit = UIkit;

// Font CSS — imported here (not from SCSS) so css-loader can resolve the url()
// paths and webpack copies the WOFF2 files to assets/fonts/. The SCSS pipeline
// has url:false set, which would leave these paths unresolved at runtime.
// Montserrat — latin subset only. A local CSS file declares only the two latin
// @font-face rules; css-loader resolves the woff2 url()s so webpack copies
// just those two files to assets/fonts/.
import '../css/montserrat-latin.css';

// The SCSS entry point is imported here so webpack compiles both in one pass.
import '../scss/main.scss';

initCounters();
initFeaturedPostSliders();
initCodeBlocks();
initClipboard();
initLucide();
initBlogLoadMore();
initVideoCards();
initCourseGrid();
initLoadMore( {
    gridId: 'video-grid',
    btnId:  'video-load-more',
    wrapId: 'video-load-more-wrap',
    action: 'ohp_load_more_videos',
} );

// =============================================================================
// Theme JavaScript
// Add your own JS below. UIkit is available as a module-level import above,
// or globally as `window.UIkit` (set by UIkit itself).
// =============================================================================

// uk-inverse only samples while the bar sits at the top (.ohp-nav-top). Pages
// that start under a dark hero keep light mode (.ohp-nav-light) while sticky,
// so the dark blur bar stays for the whole scroll.
( function () {
    const navbar = document.querySelector( 'header.uk-navbar-container[uk-inverse]' );
    if ( ! navbar ) return;

    let lightAtTop = false;

    const sync = () => {
        const atTop = navbar.classList.contains( 'ohp-nav-top' );
        if ( atTop ) lightAtTop = navbar.classList.contains( 'uk-light' );

        const wantLight = ! atTop && lightAtTop;
        // Only touch the class when it changes, or the observer would loop.
        if ( navbar.classList.contains( 'ohp-nav-light' ) !== wantLight ) {
            navbar.classList.toggle( 'ohp-nav-light', wantLight );
        }
    };

    new MutationObserver( sync ).observe( navbar, { attributes: true, attributeFilter: [ 'class' ] } );
    sync();
} )();


// Example: programmatic notification
// UIkit.notification({ message: 'Site loaded', status: 'primary' });

(function () {
  // If the browser supports scroll-driven animations, do nothing — CSS handles it.
  if (CSS.supports('animation-timeline', 'view()')) return;

  // ── JS fallback for Firefox and other non-supporting browsers ──
  // Add transition class now that we know CSS animation won't run
  const headings = document.querySelectorAll('.heading-highlight');
  headings.forEach(el => el.classList.add('js-animate'));

  // Use IntersectionObserver to detect when element enters viewport,
  // then use scroll position to drive background-size proportionally.
  const observers = new Map();

  function trackScroll(el) {
    function onScroll() {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;

      // progress: 0 when bottom of element hits bottom of viewport,
      //           1 when element is 60% up the viewport
      const start = vh;           // element enters at bottom
      const end   = vh * 0.4;     // fully revealed when top is 40% from bottom
      const pos   = rect.bottom;

      const progress = Math.min(1, Math.max(0, (start - pos) / (start - end)));

      el.style.backgroundSize = (progress * 100) + '% 100%';

      if (progress >= 1) {
        window.removeEventListener('scroll', onScroll, { passive: true });
        observers.get(el)?.disconnect();
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    observers.set(el, null); // placeholder so we can clean up

    // Run once immediately in case already in view
    onScroll();
  }

  // Only start tracking scroll when element is near the viewport
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        trackScroll(entry.target);
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  headings.forEach(el => io.observe(el));
})();

// =============================================================================
// Animated background size on .heading-highlight elements as they scroll into view.
// Uses CSS animation-timeline where supported, with a JS fallback for other browsers.
// The JS fallback is not pixel-perfect but provides a similar effect without
// causing jank by running on every scroll frame.
// =============================================================================

(function () {
  // If the browser supports scroll-driven animations, do nothing — CSS handles it.
  if (CSS.supports('animation-timeline', 'view()')) return;

  // ── JS fallback for Firefox and other non-supporting browsers ──
  // Add transition class now that we know CSS animation won't run
  const headings = document.querySelectorAll('.heading-highlight');
  headings.forEach(el => el.classList.add('js-animate'));

  // Use IntersectionObserver to detect when element enters viewport,
  // then use scroll position to drive background-size proportionally.
  const observers = new Map();

  function trackScroll(el) {
    function onScroll() {
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;

      // progress: 0 when bottom of element hits bottom of viewport,
      //           1 when element is 60% up the viewport
      const start = vh;           // element enters at bottom
      const end   = vh * 0.4;     // fully revealed when top is 40% from bottom
      const pos   = rect.bottom;

      const progress = Math.min(1, Math.max(0, (start - pos) / (start - end)));

      el.style.backgroundSize = (progress * 100) + '% 100%';

      if (progress >= 1) {
        window.removeEventListener('scroll', onScroll, { passive: true });
        observers.get(el)?.disconnect();
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    observers.set(el, null); // placeholder so we can clean up

    // Run once immediately in case already in view
    onScroll();
  }

  // Only start tracking scroll when element is near the viewport
  const io = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        trackScroll(entry.target);
        io.unobserve(entry.target);
      }
    });
  }, { rootMargin: '0px 0px -10% 0px' });

  headings.forEach(el => io.observe(el));
})();




// =============================================================================
// Back-to-top button
// Shows after the user scrolls past 100vh; hides when back at the top.
// =============================================================================

(function () {
    const btn = document.querySelector( '.site-back-to-top' );
    if ( ! btn ) return;

    const threshold = window.innerHeight * 0.5;

    function update() {
        btn.classList.toggle( 'is-visible', window.scrollY > threshold );
    }

    window.addEventListener( 'scroll', update, { passive: true } );
    update();
})();

// =============================================================================
// End of main.js
// Add any additional scripts above this line.
// =============================================================================